import { useTranslation } from 'hooks/useTranslation'
import Image from 'next/dist/client/image'
import { Button } from 'components/Button'
import { Header } from 'components/Header'
import { Layout } from 'components/Layout'
import { Stack } from 'components/Stack'
import { PostContent, PostContentType } from './components/PostContent'
import { PostImage, PostImageType } from './components/PostImage'
import { PostBanner, PostBannerType } from './components/BlogBanner'
import { PostTableOfContents } from './components/PostTableOfContents'
import { PostTitle } from './components/PostTitle'
import {
  BlogHeroBlob,
  PostRoot,
  BlogPatternHero,
  ContentCard,
  Hero,
  ImageWrapper,
  TitleWrapper,
  Wrapper,
  Background,
  Tag,
  SocialMediaShareContainer,
} from './Post.styles'

import * as isotypeYellow from '../../../../../public/assets/isotype-yellow.svg'
import * as blogPatternHero from '../../../../../public/assets/blog-pattern-hero-2.webp'
import { Footer } from 'components/Footer'
import { RelatedPosts } from './components/RelatedPosts'
import { FacebookShareButton, TwitterShareButton } from 'next-share'
import { Icon } from 'components/Icon'
import { capitalize } from 'utils/capitalize'
import { Desktop, Mobile } from 'components/Responsive/Mobile'
import { Link } from 'components/Link'
import { Text } from 'components/Text/Text'

export type BlogPostItemFields = {
  metaTitle: string
  metaDescription: string
  pageTitle: string
  headerImage: PostImageType
  postTitle: string
  postContent1: PostContentType
  banner: PostBannerType
  postContent2: PostContentType
  postUrl: string
  postSummary: string
  schema: Object
  postAuthor: string
  postReviewer?: string
}

export type BlogPostMetadata = {
  tags: { sys: { id: string } }[]
}

export type BlogPostSystemFields = {
  createdAt: Date
  updatedAt: Date
}

type PostProps = {
  post: {
    fields: BlogPostItemFields
    metadata: BlogPostMetadata
    sys: BlogPostSystemFields
  }
  relatedPosts: {
    items: {
      fields: BlogPostItemFields
      metadata: BlogPostMetadata
      sys: BlogPostSystemFields
    }[]
  }
  postAuthor: string
  postReviewer?: string
}

export const Post = ({ post, relatedPosts, postAuthor, postReviewer }: PostProps) => {
  const { t } = useTranslation()

  const CTA = (
    <Link href="/meal-plan" passHref>
      <Button as="a" className="header-cta" size="small">
        {t('blog:cta')}
      </Button>
    </Link>
  )

  const header = <Header snapToTop isFixed customCTA={CTA} />
  return (
    <Layout header={header} CTA={{ text: 'blog:cta', url: '/' }}>
      <PostRoot>
        <Wrapper>
          {post.fields.headerImage.fields?.file && (
            <Hero>
              <BlogPatternHero>
                <Image src={blogPatternHero} alt="" />
              </BlogPatternHero>

              <ImageWrapper>
                <PostImage content={post.fields.headerImage} />
              </ImageWrapper>

              <BlogHeroBlob>
                <Image src={isotypeYellow} alt="" />
              </BlogHeroBlob>
            </Hero>
          )}
          <Background>
            <Stack direction="column" gap="1">
              <SocialMediaShareContainer gap="3">
                <Desktop breakpoint="bp2">
                  {post.metadata.tags?.[0]?.sys.id && (
                    <Tag>{capitalize(post.metadata.tags[0].sys.id || '')}</Tag>
                  )}
                </Desktop>
                <FacebookShareButton url={`https://www.wefeedraw.com/blog/${post.fields.postUrl}`}>
                  <Icon name="facebook" size="4" />
                </FacebookShareButton>
                <TwitterShareButton url={`https://www.wefeedraw.com/blog/${post.fields.postUrl}`}>
                  <Icon name="twitter" size="4" />
                </TwitterShareButton>
              </SocialMediaShareContainer>
            </Stack>
            <ContentCard>
              <Wrapper>
                <Stack direction="column" gap="10">
                  <Stack direction="column" gap="3">
                    <Mobile breakpoint="bp2">
                      {post.metadata.tags?.[0]?.sys.id && (
                        <Tag>{capitalize(post.metadata.tags[0].sys.id || '')}</Tag>
                      )}
                    </Mobile>
                    {post.fields.postTitle && (
                      <TitleWrapper>
                        <PostTitle content={post.fields.postTitle} />
                      </TitleWrapper>
                    )}
                    <Stack direction="column">
                      <Stack direction="row" gap="0">
                        <Text transform="uppercase" weight="semiBold">
                          {t('blog:by')}&nbsp;
                        </Text>
                        <Text transform="uppercase" weight="semiBold" color="primary-700">
                          {postAuthor}
                        </Text>
                      </Stack>
                      {postReviewer && (
                        <Stack direction="row" gap="0">
                          <Text transform="uppercase" weight="semiBold">
                            {t('blog:reviewed-by')}&nbsp;
                          </Text>
                          <Text transform="uppercase" weight="semiBold" color="primary-700">
                            {postReviewer}
                          </Text>
                        </Stack>
                      )}
                    </Stack>
                    <Stack direction="column">
                      {post.sys.updatedAt && (
                        <Text size="caption" color="tertiary-1000" transform="uppercase">
                          {t('blog:updated', {
                            date: new Date(post.sys.updatedAt).toLocaleDateString(),
                          })}
                        </Text>
                      )}
                      {post.fields.postContent1 && (
                        <PostTableOfContents content={post.fields.postContent1} />
                      )}
                    </Stack>
                    {post.fields.postContent1 && <PostContent content={post.fields.postContent1} />}
                  </Stack>

                  {post.fields.banner && post.fields.banner.fields.bannerLink && (
                    <PostBanner content={post.fields.banner} />
                  )}

                  {post.fields.postContent2 && <PostContent content={post.fields.postContent2} />}
                </Stack>
              </Wrapper>
            </ContentCard>
            <RelatedPosts relatedPosts={relatedPosts.items} />
          </Background>
        </Wrapper>
      </PostRoot>
      <Footer />
    </Layout>
  )
}
